// =============================================================================
// hooks/kanban/board.ts — Lógica pura do board paginado por estágio (F55-S08).
//
// Sem React, sem rede: tudo aqui é testável isolado. Os hooks
// (useKanbanCards, useMoveCard) só orquestram cache + fetch em cima disto.
//
// Contrato com a API:
//   GET /api/kanban/cards?stage_id=&cursor=&limit=&<filtros>
//       → { cards, total, nextCursor }   (página de UMA coluna)
//   GET /api/kanban/cards/counts?<filtros>
//       → { counts: [{ stageId, total }], total }   (mesmo WHERE da lista)
//
// Ordem do servidor: priority DESC, updated_at DESC, id DESC. O cliente NUNCA
// reordena o que veio do servidor — só insere o card movido no lugar certo.
// =============================================================================

import type { KanbanCard, KanbanFilters } from './types';

/** Tamanho da página de cada coluna. */
export const KANBAN_PAGE_SIZE = 50;

/** Teto do filtro `lead_ids` no servidor (schemas.ts MAX_LEAD_IDS_FILTER). */
export const MAX_LEAD_IDS_FILTER = 100;

/** Uma página como a API devolve. */
export interface KanbanCardsPage {
  cards: KanbanCard[];
  total: number;
  nextCursor: string | null;
}

/** Estado acumulado de uma coluna: todas as páginas já carregadas, em ordem. */
export interface StageCards {
  cards: KanbanCard[];
  /** Cursor para a próxima página; null = coluna completa. */
  nextCursor: string | null;
}

export interface KanbanCountsResponse {
  counts: Array<{ stageId: string; total: number }>;
  total: number;
}

/** stageId → total da coluna. Estágio ausente = 0. */
export type StageCounts = Record<string, number>;

// ── Query string ─────────────────────────────────────────────────────────────

/**
 * Filtros → querystring da API. A MESMA função alimenta lista e contagem: se
 * divergissem, o contador mentiria sobre o que a coluna pagina.
 */
export function filtersToParams(filters: KanbanFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.cityId) params.set('city_id', filters.cityId);
  if (filters.agentId) params.set('agent_id', filters.agentId);
  if (filters.minAmountCents !== undefined) {
    params.set('min_amount_cents', String(filters.minAmountCents));
  }
  if (filters.maxAmountCents !== undefined) {
    params.set('max_amount_cents', String(filters.maxAmountCents));
  }
  if (filters.dateFrom) params.set('date_from', filters.dateFrom);
  if (filters.dateTo) params.set('date_to', filters.dateTo);
  if (filters.leadIds !== undefined && filters.leadIds.length > 0) {
    params.set('lead_ids', filters.leadIds.join(','));
  }
  return params;
}

/**
 * Valor digitado em reais → centavos inteiros (a API exige inteiro ≥ 0).
 * `1500.5 * 100` dá 150050.00000000003 em float — sem o round viraria 400.
 * Vazio, inválido ou negativo = sem filtro.
 */
export function reaisToCents(raw: string): number | undefined {
  if (raw.trim() === '') return undefined;
  const value = Number(raw);
  if (!Number.isFinite(value) || value < 0) return undefined;
  return Math.round(value * 100);
}

/** Querystring de uma página de coluna. */
export function stagePageParams(
  filters: KanbanFilters,
  stageId: string,
  cursor: string | null,
  limit: number = KANBAN_PAGE_SIZE,
): URLSearchParams {
  const params = filtersToParams(filters);
  params.set('stage_id', stageId);
  params.set('limit', String(limit));
  if (cursor !== null) params.set('cursor', cursor);
  return params;
}

/**
 * Filtro rápido sem nenhum lead ("Retorno hoje" vazio) não pode virar "sem
 * filtro" — o board fica vazio sem nem perguntar ao servidor.
 */
export function isEmptyLeadFilter(filters: KanbanFilters): boolean {
  return filters.leadIds !== undefined && filters.leadIds.length === 0;
}

/**
 * Normaliza os filtros para chave de cache: `lead_ids` ordenado e sem
 * duplicata (o Set do CRM não tem ordem estável), teto do servidor aplicado.
 */
export function normalizeFilters(filters: KanbanFilters): KanbanFilters {
  if (filters.leadIds === undefined) return filters;
  const leadIds = [...new Set(filters.leadIds)].sort().slice(0, MAX_LEAD_IDS_FILTER);
  return { ...filters, leadIds };
}

// ── Páginas ──────────────────────────────────────────────────────────────────

/**
 * Acrescenta uma página ao acumulado da coluna. Dedup por id: se um card foi
 * movido para cá de forma otimista e depois chega na página do servidor, ele
 * não aparece duas vezes.
 */
export function appendPage(prev: StageCards | undefined, page: KanbanCardsPage): StageCards {
  const base = prev?.cards ?? [];
  const seen = new Set(base.map((c) => c.id));
  const fresh = page.cards.filter((c) => !seen.has(c.id));
  return { cards: [...base, ...fresh], nextCursor: page.nextCursor };
}

/** Ordem do servidor: priority (position) DESC, updatedAt DESC, id DESC. */
export function compareCards(a: KanbanCard, b: KanbanCard): number {
  if (a.position !== b.position) return b.position - a.position;
  if (a.updatedAt !== b.updatedAt) return a.updatedAt < b.updatedAt ? 1 : -1;
  if (a.id === b.id) return 0;
  return a.id < b.id ? 1 : -1;
}

export function removeCard(stage: StageCards, cardId: string): StageCards {
  return { ...stage, cards: stage.cards.filter((c) => c.id !== cardId) };
}

/**
 * Insere o card na posição da ordem do servidor. Se a coluna tem mais páginas
 * e o card cairia DEPOIS do último carregado, ele pertence a uma página ainda
 * não vista — não entra agora (aparece ao "carregar mais"; o contador já o
 * conta).
 */
export function insertCard(stage: StageCards, card: KanbanCard): StageCards {
  const without = stage.cards.filter((c) => c.id !== card.id);
  const idx = without.findIndex((c) => compareCards(card, c) < 0);
  if (idx === -1) {
    if (stage.nextCursor !== null) return { ...stage, cards: without };
    return { ...stage, cards: [...without, card] };
  }
  return { ...stage, cards: [...without.slice(0, idx), card, ...without.slice(idx)] };
}

// ── Contagem ─────────────────────────────────────────────────────────────────

export function countsToRecord(res: KanbanCountsResponse): StageCounts {
  const out: StageCounts = {};
  for (const c of res.counts) out[c.stageId] = c.total;
  return out;
}

/** Move otimista no contador: origem −1 (nunca negativo), destino +1. */
export function applyMoveToCounts(
  counts: StageCounts,
  fromStageId: string,
  toStageId: string,
): StageCounts {
  if (fromStageId === toStageId) return counts;
  return {
    ...counts,
    [fromStageId]: Math.max(0, (counts[fromStageId] ?? 0) - 1),
    [toStageId]: (counts[toStageId] ?? 0) + 1,
  };
}

/** Quantos cards da coluna ainda não foram carregados (nunca negativo). */
export function remainingInStage(total: number | undefined, loaded: number): number {
  if (total === undefined) return 0;
  return Math.max(0, total - loaded);
}

/** Card movido como o servidor vai devolvê-lo: novo estágio, updated_at agora. */
export function movedCard(card: KanbanCard, toStageId: string, now: Date): KanbanCard {
  return { ...card, stageId: toStageId, updatedAt: now.toISOString() };
}
